import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  DOCUMENT_TYPE_LABEL,
  DOCUMENT_TYPE_LABEL_KEY,
  OPERATION_LABEL,
  OPERATION_LABEL_KEY,
  OUTCOME_LABEL,
  OUTCOME_LABEL_KEY,
  useFiscalSignatureAudit,
} from '@/hooks/use-fiscal-cabinet';
import { useT } from '@/i18n/use-t';
import { EmptyPanel, FiscalCabinetSection, Pager } from './fiscal-cabinet-section';

const PAGE_SIZE = 50;

function SignatureLog({ available }: { available: boolean }) {
  const t = useT('admin_ui');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useFiscalSignatureAudit(
    { page, pageSize: PAGE_SIZE },
    available,
  );

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  }
  if (isError || data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
      </p>
    );
  }
  if (data.items.length === 0) {
    return (
      <EmptyPanel>
        {t(
          'fiscalCabinet.signatureLog.empty',
          'No signatures yet. Every signing attempt, successful or not, is recorded here.',
        )}
      </EmptyPanel>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('fiscalCabinet.signatureLog.col.time', 'Time')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.operation', 'Operation')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.document', 'Document')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.hash', 'Document hash')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.keySerial', 'Key serial')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.outcome', 'Result')}</TH>
              <TH>{t('fiscalCabinet.signatureLog.col.error', 'Error')}</TH>
            </TR>
          </THead>
          <TBody>
            {data.items.map((row) => (
              <TR key={row.id}>
                <TD className="whitespace-nowrap font-mono text-xs">
                  {row.createdAt.slice(0, 19).replace('T', ' ')}
                </TD>
                <TD>{t(OPERATION_LABEL_KEY[row.operation], OPERATION_LABEL[row.operation])}</TD>
                <TD>
                  {t(
                    DOCUMENT_TYPE_LABEL_KEY[row.documentType],
                    DOCUMENT_TYPE_LABEL[row.documentType],
                  )}
                </TD>
                <TD className="font-mono text-xs" title={row.documentSha256}>
                  {row.documentSha256.slice(0, 12)}…
                </TD>
                <TD className="font-mono text-xs">{row.keySerial ?? '—'}</TD>
                <TD>
                  <Badge variant={row.outcome === 'signed' ? 'success' : 'destructive'}>
                    {t(OUTCOME_LABEL_KEY[row.outcome], OUTCOME_LABEL[row.outcome])}
                  </Badge>
                </TD>
                <TD className="text-xs text-muted-foreground" title={row.errorMessage ?? undefined}>
                  {row.errorCode ?? '—'}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
    </div>
  );
}

/** PFC-9 — Fiscal cabinet → Signature log. */
export function FiscalSignatureLogPage() {
  return (
    <FiscalCabinetSection>
      {(available) => <SignatureLog available={available} />}
    </FiscalCabinetSection>
  );
}
